import { Pageable } from 'ws/Pageable';
import {
  BeaconConnectionOption,
  BeaconMessageFormType,
  BeaconMessagesWithPagination
} from './BeaconMessage';
import {
  BeaconMessageWebService,
  RestfulBeaconMessageWebService
} from 'ws/BeaconMessageWebService';
import {
  CloudStorageManager,
  DefaultCloudStorageManager
} from 'core/cloudStorage/CloudStorageManager';
import {
  LineMessage,
  LineMessageFormData,
  LineMessageType
} from 'core/advertiser/LineBeacon';
import {
  CreativeManager,
  DefaultCreativeManager
} from 'core/creative/CreativeManager';
import { omit } from 'lodash';
import i18n from 'i18n';

export interface BeaconMessageManager {
  getBeaconMessages(
    agencyId: number | string | undefined,
    beaconConnectionId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<BeaconMessagesWithPagination>;
  getBeaconConnectionOptions(): Promise<BeaconConnectionOption[]>;
  createBeaconMessage(beaconMessage: BeaconMessageFormType): Promise<void>;
  updateBeaconMessage(
    l2ObjectId: number | string,
    beaconMessage: BeaconMessageFormType
  ): Promise<void>;
  getBeaconMessage(l2ObjectId: number | string): Promise<BeaconMessageFormType>;
  updateBeaconMessagesState(
    beaconMessageIds: number[],
    state: 'activate' | 'deactivate'
  ): Promise<void>;
}

export class DefaultBeaconMessageManager implements BeaconMessageManager {
  constructor(
    private beaconMessageWebService: BeaconMessageWebService = new RestfulBeaconMessageWebService(),
    private cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager(),
    private creativeManager: CreativeManager = new DefaultCreativeManager()
  ) {}

  async getBeaconMessages(
    agencyId: number | string | undefined,
    beaconConnectionId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ) {
    return this.beaconMessageWebService.getBeaconMessages(
      agencyId,
      beaconConnectionId,
      pageable,
      search
    );
  }

  async getBeaconConnectionOptions(): Promise<BeaconConnectionOption[]> {
    return this.beaconMessageWebService.getBeaconConnectionOptions();
  }

  async getBeaconMessage(
    l2ObjectId: number | string
  ): Promise<BeaconMessageFormType> {
    return this.beaconMessageWebService.getBeaconMessage(l2ObjectId);
  }

  async createBeaconMessage(
    beaconMessage: BeaconMessageFormType
  ): Promise<void> {
    return this.beaconMessageWebService.createBeaconMessage(
      await this.toBeaconMessageDTO(beaconMessage)
    );
  }

  async updateBeaconMessage(
    l2ObjectId: number | string,
    beaconMessage: BeaconMessageFormType
  ): Promise<void> {
    return this.beaconMessageWebService.updateBeaconMessage(
      l2ObjectId,
      await this.toBeaconMessageDTO(beaconMessage)
    );
  }

  async updateBeaconMessagesState(
    beaconMessageIds: number[],
    state: 'activate' | 'deactivate'
  ): Promise<void> {
    return this.beaconMessageWebService.updateBeaconMessagesState(
      beaconMessageIds,
      state
    );
  }

  private async toBeaconMessageDTO(beaconMessage: BeaconMessageFormType) {
    const processLineMessage = async (
      message: LineMessageFormData
    ): Promise<LineMessage> => {
      const result: any = {
        type: message.type
      };
      if (message.type === LineMessageType.TEXT) {
        result.message = message.message;
      } else if (message.type === LineMessageType.IMAGE) {
        const imageFile = message.image.file;
        const imageUrl = message.image.url;
        if (imageFile) {
          const url =
            await this.cloudStorageManager.uploadFileToCloud(imageFile);
          result.imageUrl = url;
        } else if (imageUrl) {
          result.imageUrl = imageUrl;
        }
      } else if (message.type === LineMessageType.VIDEO) {
        const videoFile = message.video.file;
        const videoUrl = message.video.url;
        if (videoFile) {
          const assetId =
            await this.cloudStorageManager.uploadVideoToCloud(videoFile);
          if (assetId) {
            const videoUrl = await this.creativeManager.publicVideoAssetId(
              assetId,
              beaconMessage.creative.creativeType
            );
            result.videoUrl = videoUrl;
          }
        } else if (videoUrl) {
          result.videoUrl = videoUrl;
        }
        const previewImageFile = message.previewImage.file;
        const previewImageUrl = message.previewImage.url;
        if (previewImageFile) {
          const url =
            await this.cloudStorageManager.uploadFileToCloud(previewImageFile);
          result.previewImageUrl = url;
        } else if (previewImageUrl) {
          result.previewImageUrl = previewImageUrl;
        }
      } else if (message.type === LineMessageType.JOIN) {
        result.officialAccount = message.officialAccount;
      } else if (message.type === LineMessageType.COUPON) {
        result.couponId = message.couponId;
      }
      return result;
    };

    const lineMessage: (LineMessage & { order: number })[] = [];
    for (let i = 0; i < beaconMessage.creative.lineMessage.length; ++i) {
      const message = beaconMessage.creative.lineMessage[i];
      const processedLineMessage = await processLineMessage(message);
      lineMessage.push({
        ...processedLineMessage,
        order: i
      });
    }
    return {
      ...beaconMessage,
      limitations: [
        {
          op: 'inc',
          limits: beaconMessage.creative.typeProperties.stores.map((store) => ({
            label: i18n.t(`store.${store}`),
            value: store
          })),
          isGroup: false,
          type: 'storeNum'
        }
      ],
      creative: {
        ...beaconMessage.creative,
        name: beaconMessage.name,
        lineMessage,
        typeProperties: {
          ...omit(beaconMessage.creative.typeProperties, 'stores'),
          maxFrequency:
            beaconMessage.creative.typeProperties.maxFrequency.toString(),
          intervalHours:
            beaconMessage.creative.typeProperties.intervalHours.toString(),
          beaconConnectionId:
            beaconMessage.creative.typeProperties.beaconConnection!.value.toString()
        }
      }
    };
  }
}
